import React, { useState } from 'react';
import { ChevronLeft, Bookmark, PlayCircle, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  useFinancialContents, 
  useBookmarks, 
  useFinancialCategories, 
  useBookmarkContent, 
  useDeleteBookmark 
} from '../hooks/financialInsights/useFinancialInsights';
import DashboardFooter from '../pages/dashboard/DashboardFooter';
import PatternBackground from '../components/PatternBackground';

const FinancialInsight = () => {
  const navigate = useNavigate(); // Add this line
    const [activeCategory, setActiveCategory] = useState('All');
  
    const handleBack = () => navigate(-1);

  // Data fetching
  const { data: contents } = useFinancialContents({});
  const { data: bookmarks } = useBookmarks();
  const { data: categories } = useFinancialCategories();
  const bookmarkMutation = useBookmarkContent();
  const deleteBookmarkMutation = useDeleteBookmark();

  const handleBookmarkToggle = (content) => {
    const isBookmarked = bookmarks?.some(b => b.contentId === content.id);
    if (isBookmarked) {
      const bookmarkId = bookmarks.find(b => b.contentId === content.id).id;
      deleteBookmarkMutation.mutate({ id: bookmarkId });
    } else {
      bookmarkMutation.mutate({ contentId: content.id });
    }
  };

  const featuredContent = (Array.isArray(contents) ? contents : []).slice(0, 4);
  const listContent = (Array.isArray(contents) ? contents : []).slice(4);

  return (
    <div className="flex flex-col min-h-screen bg-[#0f1117]">
      <PatternBackground>
        <div className="w-full px-8 pb-20 relative z-[2] flex-1">
          {/* Top Navigation - Full Width, Left Aligned */}
          <div className="sticky top-0 z-10 border-b border-white/5 px-4 py-3 flex items-center">
            <button onClick={handleBack} className="p-2 hover:bg-white/10 rounded-full transition" aria-label="Go back">
              <ChevronLeft size={24} />
            </button>
            <h1 className="ml-3 text-lg font-semibold">Financial Insights</h1>
          </div>
          
          {/* Featured Horizontal Scroll */}
          <div className="px-6 overflow-x-auto flex gap-4 no-scrollbar pb-6">
            {featuredContent.map((item) => (
              <div key={item.id} className="min-w-[280px] bg-[#2D2B3F] rounded-2xl overflow-hidden flex-shrink-0 border border-white/5">
                <div className="h-32 bg-gray-700 relative">
                  <img src={item.imageUrl || "/api/placeholder/400/200"} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-sm mb-1">{item.title}</h3>
                  <p className="text-[10px] text-slate-400 line-clamp-2">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Categories */}
          <div className="px-6 mb-6">
            <h2 className="text-sm font-bold mb-4">Browse by Category</h2>
            <div className="flex gap-3 overflow-x-auto no-scrollbar">
              <button 
                onClick={() => setActiveCategory('All')}
                className={`px-6 py-2 rounded-full text-xs font-medium transition ${activeCategory === 'All' ? 'bg-[#2D2B3F] border border-white/20' : 'bg-transparent border border-white/10 text-slate-400'}`}
              >
                All
              </button>
              {categories?.map((cat) => (
                <button 
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.name)}
                  className={`px-6 py-2 rounded-full text-xs font-medium transition whitespace-nowrap ${activeCategory === cat.name ? 'bg-[#2D2B3F] border border-white/20' : 'bg-transparent border border-white/10 text-slate-400'}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Content List */}
          <div className="px-6 space-y-4 pb-20">
            {listContent.map((item) => (
              <div key={item.id} className="bg-[#2D2B3F] p-5 rounded-2xl border border-white/5 flex flex-col gap-2 relative">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    {item.type === 'Video' ? <PlayCircle size={12} /> : <FileText size={12} />}
                    <span>{item.type} • {item.readTime || '6 mins read'}</span>
                  </div>
                  <button onClick={() => handleBookmarkToggle(item)}>
                    <Bookmark 
                      size={16} 
                      className={bookmarks?.some(b => b.contentId === item.id) ? "fill-white text-white" : "text-slate-400"} 
                    />
                  </button>
                </div>
                <h4 className="text-sm font-bold">{item.title}</h4>
                <p className="text-xs text-slate-400 line-clamp-1">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </PatternBackground>

      <DashboardFooter />
    </div>
  );
};

export default FinancialInsight;